/**
 * Abstract game-mode policy. The server's ServerGame owns the GameWorld and
 * orchestration; the mode plugs in collision rules, scoring, lives, win
 * conditions, and any extra map entities. Methods may mutate the world via
 * spawnAsteroidWave / etc., but must not duplicate physics work.
 */
export default class GameMode {
  static get name() { return 'base'; }

  // Static configuration queries — read once when the room transitions to PLAYING.
  get shipVsShipCollision() { return false; }
  get bulletHitsFriendly() { return false; }
  get sharedScore() { return true; }
  get initialLives() { return 3; }
  get respawnDelay() { return 3000; }
  get maxPlayers() { return 4; }
  get teams() { return null; }
  get timedGameMs() { return null; }
  get spawnInvulnerabilityDuration() { return 0; }
  get disableFireDuringSpawnInvulnerability() { return false; }

  // Lifecycle hooks (no-ops by default).
  onGameStart(/* world, players, ctx */) {}
  onTick(/* world, tick, ctx */) {}
  onAsteroidDestroyed(/* world, asteroid, byPlayerId, ctx */) {}
  onShipHit(/* world, ship, hitBy, ctx */) {}
  onAllAsteroidsCleared(/* world, ctx */) {}
  onPlayerJoin(/* world, player, ctx */) {}
  onPlayerLeave(/* world, playerId, ctx */) {}
  onShipCollide(/* world, shipA, shipB, ctx */) {}

  // Return null to continue, or { reason, finalScore, scores } to end the game.
  checkGameOver(/* world, ctx */) { return null; }

  // Initial map entities (flags, bases, etc). Co-op returns [].
  getInitialMapEntities() { return []; }

  // Per-snapshot mode state (flag position, bases, etc). Null when unused.
  getSnapshotState(/* world, ctx */) { return null; }

  getSpawnPositions({ worldWidth, worldHeight, playerCount }) {
    const cx = worldWidth / 2
    const cy = worldHeight / 2
    if (playerCount <= 1) return [{ x: cx, y: cy, rotation: 0 }]
    const margin = 80
    const rowY = Math.min(worldHeight - margin, cy + 200)
    const maxSpan = worldWidth - 2 * margin
    const gap = Math.min(280, maxSpan / Math.max(1, playerCount))
    const total = gap * (playerCount - 1)
    const x0 = cx - total / 2
    const out = []
    for (let i = 0; i < playerCount; i++) {
      out.push({
        x: Math.max(margin, Math.min(worldWidth - margin, x0 + i * gap)),
        y: rowY,
        rotation: 0
      })
    }
    return out
  }

  getRespawnPosition(/* { worldWidth, worldHeight, player, players } */) {
    return null
  }

  canShipTakeDamage(/* ship, source */) {
    return true
  }

  canShipFire(/* ship */) {
    return true
  }
}
